/**
 * `/payroll` → the payroll parameter versions — Task U18 (Tier-3 live-tenant readiness),
 * rebuilt by roadmap 2026-09-10 E0-4 on the endpoints E0-3 added.
 * `web/shapes/payroll.md` §6a is the agreed shape.
 *
 * Gross-to-net has been computed from `wp_payroll_formulas.params` since Task 16. The
 * numbers that decide what lands in somebody's bank account — the standard month, the
 * overtime multiplier, the insurance rates, the caps and the PIT tariff — are shown here
 * to the people who sign payroll, and since E0-4 this is also where a second person signs
 * them.
 *
 * ── The four requests ───────────────────────────────────────────────────────
 *
 *   GET  /payroll/formulas/versions        every version, newest first — the history
 *   GET  /payroll/formulas                 the version in force today, the server's own
 *                                          selection (`readFormulaEffectiveOn(today)`,
 *                                          else the earliest-dated active version)
 *   POST /payroll/formulas                 `{ params, effective_from, is_active: false }`
 *   POST /payroll/formulas/{v}/activate    `{ confirmation }`
 *
 * ── The lifecycle is still not this panel's ─────────────────────────────────
 *
 * `useVersionedConfig` (U14, KTD13) owns the history read, the draft create, the
 * touched-fields draft, the activation and the Skeleton / Empty / Retry states;
 * `VersionedConfigPanel` owns what they look like. What is here is this configuration's
 * own words and the four things payroll parameters have that no other consumer has.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  1. ONE PERSON WRITES A DRAFT; A SECOND PERSON CONFIRMS AND ACTIVATES IT
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `params` and `version` are immutable — still no `PATCH` and no `DELETE` — so no row
 * carries an edit control, absent rather than disabled. What changes is a version's
 * lifecycle, through two writes only:
 *
 *  - **create** sends `is_active: false` every time, with a typed `effective_from`. The
 *    backend still activates a `POST` that omits `is_active` (roadmap E1 closes that for
 *    older clients). This panel never sends one: a version one person makes active by
 *    themselves is the gap E0-3 exists to close. The date is required; the screen never
 *    picks one.
 *  - **activate** sends `{ confirmation }`, the confirmer's own statement of what they
 *    checked against which instrument (`formulaActivateSchema`, 10..2000 characters,
 *    measured on the trimmed text the panel sends). It rides in the hook's
 *    `activation.body`, not in `preconditionOf`: it is a human fact, not a token.
 *
 * The author is offered **no** confirm control on their own version — absent, and the
 * row says why; the server's `409` is the lock, this is the honesty. A confirmed version
 * is offered none either. Whether an adoption would put version order and date order at
 * odds (`newerVersionTakingEffectFirst`, another `409`) is NOT predicted here. It is a
 * server rule over rows this panel would have to re-derive. So the form states the rule
 * before the press, and prints the server's own reason after it (`refusalReasonOf`,
 * read off that `409` and no other refusal).
 *
 * **No second press while a write is pending** (the plan's multi-target rule). "Soạn
 * bản nháp mới", every row's "Soạn bản nháp từ bản này" and "Xác nhận …", the
 * composer's "Đóng" and "Bỏ các thay đổi", and the confirmation form's "Huỷ" are absent
 * until the write in flight settles. Each of the first five resets the hook's record of
 * which write that is (`resetDraft`, `dismissWrite`), so pressing one under it would
 * sever the write from its outcome: a refusal shown nowhere, or a landing that never
 * closes its form nor reads the version in force again — and a composer offering the
 * same draft's submit a second time. "Huỷ" resets nothing, but it closes the form whose
 * refusal line is the only place a `409`'s `details.reason` is printed (final review R23).
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  2. `confirmed_note` IS THE CONFIRMATION; `confirmation_note` IS THE WARNING
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Migration 051's `confirmation_note` is the warning the ops seed stamps on numbers
 * nobody has checked. Migration 060 never clears it: `confirmed_note` is the answer,
 * written with `activated_by`/`activated_at`. So wherever this panel says "confirmed" it
 * reads `confirmed_note !== null`, and the warning stays on screen beside the answer for
 * good — `PARALLEL_RUN_CHECKLIST_VI.md` §0 makes that the go-live gate. A draft written
 * here carries neither and is shown as not confirmed, which is true.
 *
 * Two of U18's three choices still stand, re-aimed at drafts:
 *
 *  a. **A byte-identical draft cannot be submitted.** Copying an unconfirmed version
 *     unchanged changes nothing, except that the copy no longer carries the warning a
 *     confirmer ought to read. To confirm those numbers, confirm that version.
 *  b. **The composer says so before the press** whenever its base carries a warning
 *     nobody has answered.
 *  c. **A version is an immutable FULL-parameter write, so every parameter travels.**
 *     The new `params` replaces the old blob whole; a parameter left out of the POST is
 *     a parameter deleted. The composer has a field for fourteen scalars and the tariff,
 *     and it used to build `params` out of exactly that list — so "raise the overtime
 *     multiplier" also removed this tenant's `deduction_grounds` and `deduction_cap_pct`
 *     and every lawful `phat` stopped calculating (QA-G-017). Everything the composer
 *     has no field for is carried over from the base untouched (`carriedParams`), and
 *     the change list under the fields is computed from the `params` that would be SENT
 *     rather than from the field list, so a parameter nobody can see cannot change
 *     unstated.
 *
 * The third — "nothing here offers to confirm anything" — is retired on purpose. The
 * confirmation is now a request that records who, when, and what they checked: a typed
 * sentence, never a checkbox.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  3. EXACTLY ONE ROW IS IN FORCE, AND IT IS THE SERVER'S ANSWER FOR TODAY
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Several versions can be activated at once, each governing its own stretch of dates
 * (the Vietnam 2026 set is two: 1 January and 1 July). The hook wants exactly one active
 * row. The honest single answer is `GET /payroll/formulas`, the same selection
 * `createRun` makes for a period starting today. So:
 *
 *  - `isActive` means "the version in force today";
 *  - every other row says it is not the version in force (`inactiveLabel`) — once that
 *    is known. While the read is out with no answer to keep, or after it failed, the
 *    rows say it is not known yet: a failed read is not "nothing in force";
 *  - each row's own `is_active` is printed beside it (`rowState`).
 *
 * The composer starts from the version in force, unless a row's "compose from" says
 * otherwise.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  4. KTD6 — THE NUMBERS ARE DATA, AND THIS FILE HOLDS NONE OF THEM
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Not one rate, bracket, cap or threshold is written here:
 *
 *  - every value came off the wire, and every value in the composer starts from a
 *    version;
 *  - the numeric inputs carry no `min`/`max`/`step`, and a refusal renders the server's
 *    own sentence;
 *  - the only bounds used here, the confirmation's 10..2000 characters, are text
 *    bounds, transcribed in `api/payroll.ts` and pinned against `formulaActivateSchema`
 *    by `api/payroll.test.ts`.
 *
 * A company with no version gets blank fields: this screen has no legitimate way to
 * invent a statutory number.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { useSession } from '../../auth/SessionProvider';
import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import type { MessageKey } from '../../i18n/messages';
import {
  PAYROLL_FORMULAS_PATH,
  PAYROLL_FORMULA_CONFIRMATION_MAX,
  PAYROLL_FORMULA_CONFIRMATION_MIN,
  PAYROLL_FORMULA_REQUIRED_PARAMS,
  PAYROLL_FORMULA_SCALAR_PARAMS,
  PAYROLL_FORMULA_VERSIONS_PATH,
  isPayrollFormulaAbsent,
  isUsableConfirmation,
  payrollFormulaActivatePath,
  payrollFormulaWireParams,
  payrollRunsPath,
  readPayrollFormulaBrackets,
  readPayrollFormulaGrounds,
  readPayrollFormulaNumber,
  refusalReasonOf,
  type PayrollDeductionAppliesTo,
  type PayrollFormula,
  type PayrollFormulaCreateParams,
  type PayrollFormulaDraft,
  type PayrollFormulaDraftCreateRequest,
  type PayrollFormulaListResponse,
  type PayrollFormulaScalarParam,
  type PayrollPitBracket,
  type PayrollRunListResponse,
} from '../../api/payroll';
import { useApiQuery } from '../../api/useApiQuery';
import { VersionedConfigPanel } from '../../config/VersionedConfigPanel';
import { useVersionedConfig } from '../../config/useVersionedConfig';
import { formatVnd } from '../../payroll/status';
import { DateField } from '../../components/DateField';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { SEVERITY_ICON } from '../../states/icons';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';

export interface PayrollFormulasPanelProps {
  /**
   * May this session read `GET /payroll/runs` (`payroll.view_payroll`)?
   *
   * The runs list is the only published fact about which formula version a payroll run
   * was stamped with (`PayrollRun.formula_version`), and it is a different permission
   * code from the `payroll.manage_regulations` that gates this panel. `false` parks the
   * read with no request in flight and the panel says the usage is unknown rather than
   * saying "not used by any run", which is the absence-as-zero mistake.
   */
  readonly canViewRuns: boolean;
}

/** How one parameter's value should be read on screen. Presentation, not arithmetic. */
type ParamUnit = 'hours' | 'factor' | 'fraction' | 'vnd';

/**
 * The Vietnamese name of each parameter — a `Record` on the contract's own key union, so a
 * statutory key added to `PayrollFormulaParams` and not named here is a type error.
 */
const PARAM_LABEL: Readonly<Record<PayrollFormulaScalarParam, MessageKey>> = {
  gio_chuan: 'payrollFormulas.param.gio_chuan',
  ot_coefficient: 'payrollFormulas.param.ot_coefficient',
  bhxh_nv_pct: 'payrollFormulas.param.bhxh_nv_pct',
  bhyt_nv_pct: 'payrollFormulas.param.bhyt_nv_pct',
  kpcd_nv_pct: 'payrollFormulas.param.kpcd_nv_pct',
  bhxh_cty_pct: 'payrollFormulas.param.bhxh_cty_pct',
  bhyt_cty_pct: 'payrollFormulas.param.bhyt_cty_pct',
  kpcd_cty_pct: 'payrollFormulas.param.kpcd_cty_pct',
  bhtn_nv_pct: 'payrollFormulas.param.bhtn_nv_pct',
  bhtn_cty_pct: 'payrollFormulas.param.bhtn_cty_pct',
  bhxh_bhyt_cap_vnd: 'payrollFormulas.param.bhxh_bhyt_cap_vnd',
  bhtn_cap_vnd: 'payrollFormulas.param.bhtn_cap_vnd',
  pit_personal_deduction_vnd: 'payrollFormulas.param.pit_personal_deduction_vnd',
  pit_dependent_deduction_vnd: 'payrollFormulas.param.pit_dependent_deduction_vnd',
};

const PARAM_UNIT: Readonly<Record<PayrollFormulaScalarParam, ParamUnit>> = {
  gio_chuan: 'hours',
  ot_coefficient: 'factor',
  bhxh_nv_pct: 'fraction',
  bhyt_nv_pct: 'fraction',
  kpcd_nv_pct: 'fraction',
  bhxh_cty_pct: 'fraction',
  bhyt_cty_pct: 'fraction',
  kpcd_cty_pct: 'fraction',
  bhtn_nv_pct: 'fraction',
  bhtn_cty_pct: 'fraction',
  bhxh_bhyt_cap_vnd: 'vnd',
  bhtn_cap_vnd: 'vnd',
  pit_personal_deduction_vnd: 'vnd',
  pit_dependent_deduction_vnd: 'vnd',
};

/**
 * Which pay a ground's deduction comes off, in words — a `Record` on the contract's own
 * enum, so a third `applies_to` added to `PayrollDeductionGround` is a type error here.
 */
const APPLIES_TO_LABEL: Readonly<Record<PayrollDeductionAppliesTo, MessageKey>> = {
  gross_before_tax: 'payrollFormulas.grounds.appliesTo.gross_before_tax',
  net_after_tax: 'payrollFormulas.grounds.appliesTo.net_after_tax',
};

const REQUIRED_PARAMS: ReadonlySet<string> = new Set(PAYROLL_FORMULA_REQUIRED_PARAMS);

/**
 * Every parameter the composer has a field for. Its complement is what `carriedParams`
 * carries over from the base untouched — see there for why that is not optional.
 */
const EDITED_PARAMS: ReadonlySet<string> = new Set<string>([
  ...PAYROLL_FORMULA_SCALAR_PARAMS,
  'pit_brackets',
]);

/** One parameter this draft would change. `null` on either side means "not carried". */
interface ParamChange {
  readonly key: string;
  readonly from: string | null;
  readonly to: string | null;
}

/**
 * A value's JSON with object keys sorted, at every depth — so two renderings of the same
 * parameter compare equal whatever order the server happened to emit their members in.
 * `null` here means the version does not carry this parameter at all, which is a
 * different fact from a parameter whose value is JSON `null`.
 */
function stableJson(value: unknown): string | null {
  if (value === undefined) return null;
  return JSON.stringify(value, (_key, member: unknown) =>
    typeof member === 'object' && member !== null && !Array.isArray(member)
      ? Object.fromEntries(Object.entries(member).sort(([a], [b]) => (a < b ? -1 : 1)))
      : member,
  );
}

/** One parameter's value as a line of text; `null` when the version does not carry it. */
function paramText(value: unknown): string | null {
  if (value === undefined) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  // A ground list or a tariff: its own JSON, which is what a versioned blob is.
  return JSON.stringify(value);
}

/** Same fallback `EmployeeDocumentsScreen` uses when a session carries no zone. */
const DEFAULT_TIMEZONE = 'Asia/Ho_Chi_Minh';

/** What `<input type="date">` hands back once a date is chosen: `YYYY-MM-DD`, or `''`. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Which form sent the last write — so a landed write closes its own form and no other. */
type Press = 'draft' | 'confirm';

/**
 * A fraction as the percentage an accountant reads off a decree. The exact stored value
 * is always shown too — a rounded rate is a different number. `toFixed` only stops
 * `0.105 * 100` printing as `10.500000000000002`.
 */
function percentOf(value: number): string {
  return String(Number((value * 100).toFixed(6)));
}

/** One touched field's raw text — see `fieldTexts` on why the draft is not enough. */
type FieldTexts = Readonly<Record<string, string>>;

/** One tariff row while it is being typed: two strings, neither yet a number. */
interface BracketText {
  readonly up_to_vnd: string;
  readonly rate: string;
}

/** What a field is worth right now, and whether it is worth anything at all. */
interface FieldValue {
  readonly text: string;
  readonly value: number | null;
  /** Text was typed and is not a number. Distinct from "cleared", which is not typed. */
  readonly invalid: boolean;
}

function parseTyped(raw: string): FieldValue {
  if (raw.trim() === '') return { text: raw, value: null, invalid: false };
  const parsed = Number(raw);
  return Number.isFinite(parsed)
    ? { text: raw, value: parsed, invalid: false }
    : { text: raw, value: null, invalid: true };
}

/** `null` is the open top bracket — an empty upper bound, not a missing one. */
function parseBracket(row: BracketText): PayrollPitBracket | null {
  const rate = parseTyped(row.rate);
  if (rate.value === null) return null;
  const bound = parseTyped(row.up_to_vnd);
  if (bound.invalid) return null;
  return { up_to_vnd: bound.value, rate: rate.value };
}

function bracketTextOf(bracket: PayrollPitBracket): BracketText {
  return {
    up_to_vnd: bracket.up_to_vnd === null ? '' : String(bracket.up_to_vnd),
    rate: String(bracket.rate),
  };
}

/** One version's parameters and tariff — for the version in force, and in the confirmation form. */
function FormulaParamsTable(props: {
  readonly formula: PayrollFormula;
  /** `u18` for the version in force; `u18-confirm` inside the confirmation form. */
  readonly testIdPrefix: string;
}): ReactNode {
  const { t } = useI18n();
  const { formula, testIdPrefix } = props;
  const brackets = readPayrollFormulaBrackets(formula.params);
  const grounds = readPayrollFormulaGrounds(formula.params);
  const capPct = readPayrollFormulaNumber(formula.params, 'deduction_cap_pct');
  return (
    <>
      <div className="wp-table-scroll">
        <table className="wp-table wp-table--fluid" data-testid={`${testIdPrefix}-params-table`}>
          <caption className="wp-visually-hidden">
            {t('payrollFormulas.params.caption', { version: String(formula.version) })}
          </caption>
          <thead>
            <tr>
              <th scope="col">{t('payrollFormulas.params.column.name')}</th>
              <th scope="col">{t('payrollFormulas.params.column.value')}</th>
            </tr>
          </thead>
          <tbody>
            {PAYROLL_FORMULA_SCALAR_PARAMS.map((key) => {
              const value = readPayrollFormulaNumber(formula.params, key);
              const unit = PARAM_UNIT[key];
              return (
                <tr key={key} data-testid={`${testIdPrefix}-param-${key}`}>
                  <th scope="row">
                    {t(PARAM_LABEL[key])} <code className="wp-small">{key}</code>
                  </th>
                  <td className="wp-small">
                    {value === null
                      ? t('payrollFormulas.param.absent')
                      : unit === 'fraction'
                        ? t('payrollFormulas.value.fraction', {
                            raw: String(value),
                            percent: percentOf(value),
                          })
                        : unit === 'vnd'
                          ? t('payrollFormulas.value.vnd', { value: formatVnd(value) })
                          : unit === 'hours'
                            ? t('payrollFormulas.value.hours', { value: String(value) })
                            : String(value)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h3 className="wp-small">{t('payrollFormulas.brackets.title')}</h3>
      {brackets === null || brackets.length === 0 ? (
        <p className="wp-small wp-muted" data-testid={`${testIdPrefix}-brackets-absent`}>
          {t('payrollFormulas.brackets.absent')}
        </p>
      ) : (
        <ul className="wp-rows" data-testid={`${testIdPrefix}-brackets`}>
          {brackets.map((bracket, index) => (
            <li
              className="wp-row wp-small"
              key={`${String(bracket.up_to_vnd)}-${String(bracket.rate)}-${String(index)}`}
              data-testid={`${testIdPrefix}-bracket-${String(index)}`}
            >
              {bracket.up_to_vnd === null
                ? t('payrollFormulas.brackets.open', { percent: percentOf(bracket.rate) })
                : t('payrollFormulas.brackets.upTo', {
                    bound: formatVnd(bracket.up_to_vnd),
                    percent: percentOf(bracket.rate),
                  })}
            </li>
          ))}
        </ul>
      )}

      {/*
        QA-G-016. `deduction_grounds` and `deduction_cap_pct` are parameters of the
        version like every other one, and they are the two that decide whether a `phat`
        is lawful and how large it may be. They are also the list the run-inputs grid
        asks an accountant to type a code out of, so leaving them off the only screen
        that publishes a version meant the configuration existed nowhere a person
        could read it. Read-only here: there is no PATCH on a version, and the
        composer has no field for them — it carries them over (§2c).
      */}
      <h3 className="wp-small">{t('payrollFormulas.grounds.title')}</h3>
      <p className="wp-small wp-muted">{t('payrollFormulas.grounds.hint')}</p>
      {grounds === null || grounds.length === 0 ? (
        <p className="wp-small wp-muted" data-testid={`${testIdPrefix}-grounds-absent`}>
          {t('payrollFormulas.grounds.absent')}
        </p>
      ) : (
        <table className="wp-table" data-testid={`${testIdPrefix}-grounds`}>
          <caption className="wp-visually-hidden">
            {t('payrollFormulas.grounds.caption', { version: String(formula.version) })}
          </caption>
          <thead>
            <tr>
              <th scope="col">{t('payrollFormulas.grounds.column.code')}</th>
              <th scope="col">{t('payrollFormulas.grounds.column.name')}</th>
              <th scope="col">{t('payrollFormulas.grounds.column.appliesTo')}</th>
              <th scope="col">{t('payrollFormulas.grounds.column.capped')}</th>
            </tr>
          </thead>
          <tbody>
            {grounds.map((ground) => (
              <tr key={ground.code} data-testid={`${testIdPrefix}-ground-${ground.code}`}>
                <th scope="row">
                  <code className="wp-small">{ground.code}</code>
                </th>
                <td className="wp-small">{ground.name}</td>
                <td className="wp-small">{t(APPLIES_TO_LABEL[ground.applies_to])}</td>
                <td className="wp-small">
                  {t(
                    ground.capped
                      ? 'payrollFormulas.grounds.capped.yes'
                      : 'payrollFormulas.grounds.capped.no',
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/*
        The cap is stated whether or not the version carries one: "no ceiling" is a
        fact about this version — a `capped` ground on it refuses — and printing
        nothing would read as "not loaded".
      */}
      <p className="wp-small" data-testid={`${testIdPrefix}-deduction-cap`}>
        {capPct === null
          ? t('payrollFormulas.cap.absent')
          : t('payrollFormulas.cap.value', {
              raw: String(capPct),
              percent: percentOf(capPct),
            })}
      </p>
    </>
  );
}

/** The facts of one version the hook's row does not carry: lifecycle, confirmation, author. */
function VersionFacts(props: {
  readonly formula: PayrollFormula;
  readonly sessionEmployeeId: string | null;
  readonly personOf: (employeeId: string | null) => string;
  readonly whenOf: (iso: string | null) => string;
}): ReactNode {
  const { t } = useI18n();
  const { formula } = props;
  return (
    <>
      <p className="wp-small" data-testid={`u18-lifecycle-${formula.id}`}>
        {t(
          formula.is_active
            ? 'payrollFormulas.lifecycle.active'
            : 'payrollFormulas.lifecycle.inactive',
        )}
      </p>
      {formula.confirmed_note === null ? (
        <p className="wp-small">
          <Badge
            severity="medium"
            icon={SEVERITY_ICON.medium}
            label={t('payrollFormulas.unconfirmed.badge')}
            testId={`u18-row-unconfirmed-${formula.id}`}
          />
        </p>
      ) : (
        <p className="wp-small">
          <Badge
            severity="low"
            icon="circle-check"
            label={t('payrollFormulas.confirmed.badge')}
            testId={`u18-row-confirmed-${formula.id}`}
          />{' '}
          <span data-testid={`u18-row-confirmed-by-${formula.id}`}>
            {t('payrollFormulas.confirmed.by', {
              who: props.personOf(formula.activated_by),
              when: props.whenOf(formula.activated_at),
            })}
          </span>{' '}
          <q data-testid={`u18-row-confirmed-note-${formula.id}`}>{formula.confirmed_note}</q>
        </p>
      )}
      {formula.confirmation_note === null ? null : (
        <p className="wp-small" data-testid={`u18-row-warning-${formula.id}`}>
          {t('payrollFormulas.row.warning', { note: formula.confirmation_note })}
        </p>
      )}
      <p className="wp-small wp-muted" data-testid={`u18-row-author-${formula.id}`}>
        {formula.created_by === null
          ? t('payrollFormulas.author.seed')
          : formula.created_by === props.sessionEmployeeId
            ? t('payrollFormulas.author.you')
            : t('payrollFormulas.author.other', { id: formula.created_by })}
      </p>
    </>
  );
}

export function PayrollFormulasPanel(props: PayrollFormulasPanelProps): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const sessionEmployeeId = user?.employee_id ?? null;
  const timezone = user?.timezone ?? DEFAULT_TIMEZONE;

  const [composing, setComposing] = useState(false);
  /** The row the composer's numbers start from; `null` means the version in force. */
  const [composeFromId, setComposeFromId] = useState<string | null>(null);
  /**
   * The raw text of every field the person has typed in, keyed by the wire key.
   *
   * The hook's draft holds `number`s, and a number cannot represent "0." on the way to
   * "0.105", so the text is the input's value and the draft is the parsed truth, written
   * through on every keystroke. A key absent here was never touched, and its value comes
   * from the base version.
   */
  const [fieldTexts, setFieldTexts] = useState<FieldTexts>({});
  /** `null` until the tariff is touched at all — then it is the whole editable table. */
  const [bracketTexts, setBracketTexts] = useState<readonly BracketText[] | null>(null);
  /** The draft's `effective_from`, as typed. Never defaulted — header §1. */
  const [effectiveFrom, setEffectiveFrom] = useState('');
  /** The row whose confirmation form is open. */
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const [lastPress, setLastPress] = useState<Press | null>(null);
  const [inForceToken, setInForceToken] = useState(0);

  /* ── The version in force today: the server's selection, not this file's ─── */

  const inForceRead = useApiQuery<PayrollFormula>(PAYROLL_FORMULAS_PATH, inForceToken);
  /*
    The last answer, kept while the read is out again: `useApiQuery` keeps `data` through
    a re-read and clears it on a refusal. Every landed write re-reads this, so keying on
    `status === 'ok'` would blank the in-force marker, the status note, the numbers and an
    open composer's base for every round trip.
  */
  const inForce: PayrollFormula | null = inForceRead.error === null ? inForceRead.data : null;
  /**
   * `404` naming `payroll_formula`: nothing is activated at all (drafts do not count).
   * Matched narrowly by `isPayrollFormulaAbsent` — any other `404` is a failed read.
   */
  const noneInForce =
    inForceRead.status === 'error' &&
    inForceRead.error !== null &&
    isPayrollFormulaAbsent(inForceRead.error);
  /**
   * Is it known which version is in force — one, or none at all? Not while the read is
   * out with nothing kept, and not after it failed: then no row may say it is "not the
   * version in force", which would render a failed read as "nothing in force".
   */
  const inForceKnown = inForce !== null || noneInForce;

  const state = useVersionedConfig<
    PayrollFormulaListResponse,
    PayrollFormula,
    PayrollFormulaDraft,
    PayrollFormulaDraftCreateRequest
  >({
    listPath: PAYROLL_FORMULA_VERSIONS_PATH,
    versions: (list) => list.data,
    idOf: (formula) => formula.id,
    labelOf: (formula) =>
      t('payrollFormulas.row.label', {
        version: String(formula.version),
        from: formula.effective_from,
      }),
    /* Header §3: the one row in force is the server's answer for today. */
    isActive: (formula) => inForce !== null && formula.id === inForce.id,
    fieldsOf: (formula) => {
      const fields: Record<string, string> = {
        version: String(formula.version),
        effective_from: formula.effective_from,
      };
      for (const key of PAYROLL_FORMULA_SCALAR_PARAMS) {
        const value = readPayrollFormulaNumber(formula.params, key);
        // Rendered the way the hook renders a draft `number`, so the diff compares like
        // with like.
        if (value !== null) fields[key] = String(value);
      }
      const brackets = readPayrollFormulaBrackets(formula.params);
      if (brackets !== null) fields.pit_brackets = JSON.stringify(brackets);
      return fields;
    },
    /*
      `{ kind: 'none' }`: neither write reads an `If-Match`. Two drafts saved at once are
      two drafts; two confirmations of one version are serialised by the server
      (`lockFormulasForAdoption`) and the second is refused — both said on screen
      (`payrollFormulas.concurrency`).
    */
    preconditionOf: () => ({ kind: 'none' }),
    createPath: PAYROLL_FORMULAS_PATH,
    /* Always a draft — header §1. `effective_from` is gated to a real date by `canSubmit`. */
    createBody: () => ({
      params: composedParams(),
      effective_from: effectiveFrom,
      is_active: false,
    }),
    /* No `PATCH` exists on this resource, so no row may carry an edit control. */
    patchPath: null,
    activation: {
      kind: 'endpoint',
      path: (formula) => payrollFormulaActivatePath(formula.version),
      /* Trimmed: the gate measures the trimmed text, and this is the text it measured. */
      body: () => ({ confirmation: confirmation.trim() }),
    },
  });

  const versionById = (id: string | null): PayrollFormula | null =>
    id === null ? null : (state.versions.find((formula) => formula.id === id) ?? null);
  /** Where the composer's numbers come from — header §3. */
  const base: PayrollFormula | null = composeFromId === null ? inForce : versionById(composeFromId);
  const baseParams: Readonly<Record<string, unknown>> = base?.params ?? {};
  const confirming = versionById(confirmingId);

  const personOf = (employeeId: string | null): string =>
    employeeId === null
      ? t('payrollFormulas.person.unknown')
      : employeeId === sessionEmployeeId
        ? t('payrollFormulas.person.you')
        : t('payrollFormulas.person.id', { id: employeeId });
  const whenOf = (iso: string | null): string =>
    iso === null ? t('payrollFormulas.when.unknown') : formatInstant(iso, locale, timezone);

  /**
   * Header §1: a second person, on a version nobody has confirmed yet. Nobody is offered
   * the control until the session says who they are (`/auth/me` resolves after the first
   * paint): an unknown reader is not "somebody other than the author".
   */
  const mayConfirm = (formula: PayrollFormula): boolean =>
    sessionEmployeeId !== null &&
    formula.confirmed_note === null &&
    formula.created_by !== sessionEmployeeId;
  /** The session wrote this version and nobody has confirmed it — the row says who must. */
  const ownUnconfirmed = (formula: PayrollFormula): boolean =>
    sessionEmployeeId !== null &&
    formula.confirmed_note === null &&
    formula.created_by === sessionEmployeeId;

  /* ── The runs that reference the version in force ─────────────────────────── */

  const runs = useApiQuery<PayrollRunListResponse>(props.canViewRuns ? payrollRunsPath() : null);
  const runsUsing =
    inForce === null || runs.data === null
      ? null
      : runs.data.data.filter((run) => run.formula_version === inForce.version);

  /* ── What the composer is worth right now ───────────────────────────────── */

  const storedNumber = (key: PayrollFormulaScalarParam): number | null =>
    readPayrollFormulaNumber(baseParams, key);

  const fieldOf = (key: PayrollFormulaScalarParam): FieldValue => {
    const typed = fieldTexts[key];
    if (typed !== undefined) return parseTyped(typed);
    const stored = storedNumber(key);
    return { text: stored === null ? '' : String(stored), value: stored, invalid: false };
  };

  const storedBrackets = readPayrollFormulaBrackets(baseParams);
  const typedBrackets = bracketTexts?.map(parseBracket) ?? null;
  /** `null` means "the tariff on screen is not a tariff yet", never "there is none". */
  const composedBrackets: readonly PayrollPitBracket[] | null =
    typedBrackets === null
      ? storedBrackets
      : typedBrackets.every((row) => row !== null)
        ? typedBrackets
        : null;
  const bracketsInvalid = bracketTexts !== null && composedBrackets === null;
  const bracketRows: readonly BracketText[] =
    bracketTexts ?? (storedBrackets ?? []).map(bracketTextOf);

  /**
   * Everything the base version carries that this composer has **no field for**, in the
   * request spelling — today `deduction_grounds` and `deduction_cap_pct`, tomorrow
   * whatever `PayrollFormulaParams` gains next.
   *
   * Header §2c, QA-G-017. A version is an immutable FULL-parameter write: the new
   * version's `params` replaces the old blob whole, so a parameter left out of the POST
   * is a parameter deleted. This composer used to build `params` out of its own field
   * list, which turned "raise the overtime multiplier" into "raise the overtime
   * multiplier AND remove every lawful ground for a `phat`" — and computed its diff over
   * that same list, so nothing on screen could notice.
   */
  const carriedParams: Readonly<Record<string, unknown>> = Object.fromEntries(
    Object.entries(payrollFormulaWireParams(baseParams)).filter(([key]) => !EDITED_PARAMS.has(key)),
  );

  /** The `params` object a press would send. Built from the form, never from a literal. */
  const composedParams = (): PayrollFormulaCreateParams => {
    const params: Record<string, unknown> = { ...carriedParams };
    for (const key of PAYROLL_FORMULA_SCALAR_PARAMS) {
      const { value } = fieldOf(key);
      // An optional parameter the base never carried, and nobody typed, is OMITTED
      // rather than sent as null or zero: a null in a stored law parameter is worse than
      // an absent one.
      if (value !== null) params[key] = value;
    }
    if (composedBrackets !== null && composedBrackets.length > 0) {
      params.pit_brackets = composedBrackets;
    }
    /*
      One assertion, at the boundary this function owns: the eight required keys are
      guaranteed present by `missingRequired`, which gates the control that calls this.
    */
    return params as unknown as PayrollFormulaCreateParams;
  };

  /* ── The gates ───────────────────────────────────────────────────────────── */

  const missingRequired = PAYROLL_FORMULA_REQUIRED_PARAMS.filter(
    (key) => fieldOf(key).value === null,
  );
  const invalidFields = PAYROLL_FORMULA_SCALAR_PARAMS.filter((key) => fieldOf(key).invalid);

  /**
   * What a press would change, parameter by parameter, against the base — read off the
   * `params` that would actually be SENT rather than off the composer's field list, so a
   * parameter the fields do not cover cannot change (or vanish) unstated.
   */
  const changes: readonly ParamChange[] = (() => {
    if (base === null) return [];
    const before = payrollFormulaWireParams(baseParams);
    const after = composedParams() as Readonly<Record<string, unknown>>;
    return [...new Set([...Object.keys(before), ...Object.keys(after)])]
      .filter((key) => stableJson(after[key]) !== stableJson(before[key]))
      .map((key) => ({ key, from: paramText(before[key]), to: paramText(after[key]) }));
  })();
  const changedKeys: readonly string[] = changes.map((change) => change.key);
  /** Header §2a. */
  const unchanged = base !== null && changedKeys.length === 0;
  /** Header §2b: the base carries a warning nobody has answered. */
  const baseWarned =
    base !== null && base.confirmation_note !== null && base.confirmed_note === null;
  const dateMissing = !ISO_DATE.test(effectiveFrom);

  const canSubmit =
    composing &&
    missingRequired.length === 0 &&
    invalidFields.length === 0 &&
    !bracketsInvalid &&
    !dateMissing &&
    !unchanged &&
    state.canWrite();

  const canConfirm =
    confirming !== null &&
    mayConfirm(confirming) &&
    isUsableConfirmation(confirmation) &&
    state.canWrite(confirming);
  /** The confirmation's refusal — the panel's own note renders it too, far above. */
  const confirmRefusal = lastPress === 'confirm' ? state.writeError : null;
  /*
    `details.reason` is read off the `/activate` `409` and nothing else: there it names
    the rule that fired (already confirmed, the author confirming, or version order
    against date order). Any other refusal's details are not a sentence meant for this
    line, so it says none was given and points at the panel's own note.
  */
  const confirmReason =
    confirmRefusal !== null &&
    confirmRefusal.status === 409 &&
    confirmRefusal.code === 'WORKFORCE_INVALID_STATE_TRANSITION'
      ? refusalReasonOf(confirmRefusal)
      : null;

  /*
    A landed write closes the form that sent it and no other: `saved` is true after a
    draft AND after a confirmation, and either form may be open while the other is
    pressed (the reason `FormulasScreen` keeps `submittedFromComposer`). Both re-read the
    version in force, because a confirmed adoption can move it; the hook has already
    re-read the history. `resetDraft` is not called: it would clear `saved`, the panel's
    one sentence saying the write landed.
  */
  const { saved } = state;
  useEffect(() => {
    if (!saved || lastPress === null) return;
    if (lastPress === 'draft') {
      setComposing(false);
      setComposeFromId(null);
      setFieldTexts({});
      setBracketTexts(null);
      setEffectiveFrom('');
    } else {
      setConfirmingId(null);
      setConfirmation('');
    }
    setInForceToken((value) => value + 1);
    setLastPress(null);
  }, [saved, lastPress]);

  /* ── Editing ─────────────────────────────────────────────────────────────── */

  const clearComposer = (): void => {
    setFieldTexts({});
    setBracketTexts(null);
    setEffectiveFrom('');
  };

  const openComposer = (fromId: string | null): void => {
    // What was typed against one base must not ride into another.
    state.resetDraft();
    clearComposer();
    setComposeFromId(fromId);
    setComposing(true);
  };

  const closeComposer = (): void => {
    setComposing(false);
    setComposeFromId(null);
    clearComposer();
    state.resetDraft();
  };

  const openConfirm = (id: string): void => {
    state.dismissWrite();
    setConfirmation('');
    setConfirmingId(id);
  };

  const closeConfirm = (): void => {
    setConfirmingId(null);
    setConfirmation('');
  };

  const typeField = (key: PayrollFormulaScalarParam, raw: string): void => {
    setFieldTexts((current) => ({ ...current, [key]: raw }));
    const parsed = parseTyped(raw);
    // A field cleared or mistyped is CLEARED from the draft rather than written as a
    // zero: a zero rate is a real payroll parameter and must never arrive by way of an
    // empty input.
    if (parsed.value === null) state.clearField(key);
    else state.setField(key, parsed.value);
  };

  const typeBrackets = (rows: readonly BracketText[]): void => {
    setBracketTexts(rows);
    const parsed = rows.map(parseBracket);
    if (parsed.every((row) => row !== null)) {
      state.setField('pit_brackets', parsed);
    } else {
      state.clearField('pit_brackets');
    }
  };

  /* ── Render ──────────────────────────────────────────────────────────────── */

  /*
    Absent, not disabled, while a write is pending — header §1. "Đóng" as much as "Soạn
    bản nháp mới": closing resets the hook's record of the write in flight.
  */
  const createAction: ReactNode = state.pending ? null : composing ? (
    <button type="button" className="wp-button" data-testid="u18-close" onClick={closeComposer}>
      {t('payrollFormulas.close')}
    </button>
  ) : (
    <button
      type="button"
      className="wp-button wp-button--primary"
      data-testid="u18-compose"
      onClick={() => {
        openComposer(null);
      }}
    >
      {t(
        state.versions.length === 0
          ? 'payrollFormulas.create.first'
          : 'payrollFormulas.create.draft',
      )}
    </button>
  );

  /*
    The version in force, confirmed or not — the half of E0-3 a person signs against. Its
    own region with its own Skeleton (shape §8), shown only while there is no answer to
    keep: a re-read keeps the last one on screen.
  */
  const inForceStatus: ReactNode =
    inForceRead.status === 'loading' && inForce === null ? (
      <Skeleton
        rows={2}
        variant="block"
        label={t('payrollFormulas.inForce.subject')}
        testId="u18-in-force-skeleton"
      />
    ) : noneInForce ? (
      <p className="wp-small" data-testid="u18-in-force-none">
        {t('payrollFormulas.inForce.none')}
      </p>
    ) : inForceRead.error !== null ? (
      <ApiErrorNote
        error={inForceRead.error}
        onRetry={inForceRead.reload}
        subject={t('payrollFormulas.inForce.subject')}
        testId="u18-in-force-error"
      />
    ) : inForce === null ? null : inForce.confirmed_note === null ? (
      <StatusNote
        severity="medium"
        title={t('payrollFormulas.unconfirmed.title', { version: String(inForce.version) })}
        testId="u18-unconfirmed"
      >
        <p className="wp-small">
          <Badge
            severity="medium"
            icon={SEVERITY_ICON.medium}
            label={t('payrollFormulas.unconfirmed.badge')}
            testId="u18-unconfirmed-badge"
          />{' '}
          {inForce.confirmation_note === null ? (
            <span>{t('payrollFormulas.unconfirmed.noWarning')}</span>
          ) : (
            <span data-testid="u18-unconfirmed-note">{inForce.confirmation_note}</span>
          )}
        </p>
        <p className="wp-small">{t('payrollFormulas.unconfirmed.howTo')}</p>
      </StatusNote>
    ) : (
      <div data-testid="u18-confirmed">
        <p className="wp-small">
          <Badge severity="low" icon="circle-check" label={t('payrollFormulas.confirmed.badge')} />{' '}
          {t('payrollFormulas.confirmed.by', {
            who: personOf(inForce.activated_by),
            when: whenOf(inForce.activated_at),
          })}{' '}
          <q data-testid="u18-confirmed-note">{inForce.confirmed_note}</q>
        </p>
        {inForce.confirmation_note === null ? null : (
          <p className="wp-small wp-muted" data-testid="u18-confirmed-warning">
            {t('payrollFormulas.confirmed.warningKept', { note: inForce.confirmation_note })}
          </p>
        )}
      </div>
    );

  return (
    <>
      <section
        className="wp-section"
        aria-labelledby="u18-intro-heading"
        data-testid="u18-formulas"
      >
        <h2 className="wp-section__title" id="u18-intro-heading">
          {t('payrollFormulas.title')}
        </h2>
        <p className="wp-small wp-muted">{t('payrollFormulas.intro')}</p>

        {/* What a person has to know about this backend before they press anything. */}
        <p className="wp-small wp-muted" data-testid="u18-history-note">
          {t('payrollFormulas.history')}
        </p>
        <p className="wp-small wp-muted" data-testid="u18-immutable">
          {t('payrollFormulas.immutable')}
        </p>
        <p className="wp-small wp-muted" data-testid="u18-two-person">
          {t('payrollFormulas.twoPerson')}
        </p>
        <p className="wp-small wp-muted" data-testid="u18-concurrency">
          {t('payrollFormulas.concurrency')}
        </p>
        <p className="wp-small wp-muted" data-testid="u18-in-force-note">
          {t('payrollFormulas.inForce.note')}
        </p>

        {inForceStatus}
      </section>

      <VersionedConfigPanel
        {...state}
        title={t('payrollFormulas.panel.title')}
        subject={t('payrollFormulas.subject')}
        emptyTitle={t('payrollFormulas.empty.title')}
        emptyDescription={t('payrollFormulas.empty.body')}
        {...(createAction === null ? {} : { createAction })}
        inactiveLabel={t(
          inForceKnown ? 'payrollFormulas.row.notInForce' : 'payrollFormulas.row.inForceUnknown',
        )}
        testId="u18-config"
        rowState={(row) => {
          const formula = versionById(row.id);
          return formula === null ? null : (
            <VersionFacts
              formula={formula}
              sessionEmployeeId={sessionEmployeeId}
              personOf={personOf}
              whenOf={whenOf}
            />
          );
        }}
        rowAction={(row) => {
          const formula = versionById(row.id);
          if (formula === null) return null;
          /*
            No edit control on any row, enabled or disabled: the verb does not exist. The
            two controls that are here are absent while a write is pending (header §1);
            the panel's own pending line says why.
          */
          return (
            <div className="wp-filters__actions">
              {mayConfirm(formula) ? (
                state.pending ? null : (
                  <button
                    type="button"
                    className="wp-button wp-button--primary"
                    data-testid={`u18-confirm-open-${row.id}`}
                    onClick={() => {
                      openConfirm(row.id);
                    }}
                  >
                    {t(
                      formula.is_active
                        ? 'payrollFormulas.confirm.openActive'
                        : 'payrollFormulas.confirm.openInactive',
                    )}
                  </button>
                )
              ) : ownUnconfirmed(formula) ? (
                <p className="wp-small" data-testid={`u18-confirm-self-${row.id}`}>
                  {t('payrollFormulas.confirm.self')}
                </p>
              ) : null}
              {state.pending ? null : (
                <button
                  type="button"
                  className="wp-button"
                  data-testid={`u18-compose-from-${row.id}`}
                  onClick={() => {
                    openComposer(row.id);
                  }}
                >
                  {t('payrollFormulas.row.composeFrom')}
                </button>
              )}
            </div>
          );
        }}
      />

      {confirming === null ? null : (
        <section
          className="wp-section"
          aria-labelledby="u18-confirm-heading"
          data-testid="u18-confirm"
        >
          <h2 className="wp-section__title" id="u18-confirm-heading">
            {t(
              confirming.is_active
                ? 'payrollFormulas.confirm.titleActive'
                : 'payrollFormulas.confirm.titleInactive',
              { version: String(confirming.version), from: confirming.effective_from },
            )}
          </h2>
          {/* What will happen, said before the press — header §1. */}
          <p className="wp-small" data-testid="u18-confirm-effect">
            {t(
              confirming.is_active
                ? 'payrollFormulas.confirm.effectActive'
                : 'payrollFormulas.confirm.effectInactive',
              { from: confirming.effective_from },
            )}
          </p>
          {confirming.confirmation_note === null ? null : (
            <StatusNote
              severity="medium"
              title={t('payrollFormulas.confirm.warningTitle')}
              testId="u18-confirm-warning"
            >
              <p className="wp-small">{confirming.confirmation_note}</p>
            </StatusNote>
          )}

          {/* The numbers the confirmer is attesting, on screen while they attest them. */}
          <FormulaParamsTable formula={confirming} testIdPrefix="u18-confirm" />

          {mayConfirm(confirming) ? (
            <div className="wp-field">
              <label className="wp-field__label" htmlFor="u18-confirm-text">
                {t('payrollFormulas.confirm.label')}
              </label>
              <textarea
                className="wp-field__input"
                id="u18-confirm-text"
                data-testid="u18-confirm-text"
                maxLength={PAYROLL_FORMULA_CONFIRMATION_MAX}
                value={confirmation}
                onChange={(event) => {
                  setConfirmation(event.target.value);
                }}
              />
              <p className="wp-small wp-muted" data-testid="u18-confirm-rule">
                {t('payrollFormulas.confirm.rule', {
                  min: PAYROLL_FORMULA_CONFIRMATION_MIN,
                  max: PAYROLL_FORMULA_CONFIRMATION_MAX,
                })}
              </p>
            </div>
          ) : null}

          {confirmRefusal === null ? null : (
            <p className="wp-small" role="alert" data-testid="u18-confirm-refused">
              {t('payrollFormulas.confirm.refused', {
                code: confirmRefusal.code,
                reason: confirmReason ?? t('payrollFormulas.confirm.refused.noReason'),
              })}
            </p>
          )}

          <div className="wp-filters__actions">
            {canConfirm ? (
              <button
                type="button"
                className="wp-button wp-button--primary"
                data-testid="u18-confirm-submit"
                onClick={() => {
                  setLastPress('confirm');
                  state.activate(confirming);
                }}
              >
                {t(
                  confirming.is_active
                    ? 'payrollFormulas.confirm.submitActive'
                    : 'payrollFormulas.confirm.submitInactive',
                  { version: String(confirming.version) },
                )}
              </button>
            ) : (
              <p className="wp-small" data-testid="u18-confirm-blocked">
                {!mayConfirm(confirming)
                  ? t(
                      confirming.confirmed_note === null
                        ? 'payrollFormulas.confirm.self'
                        : 'payrollFormulas.confirm.already',
                    )
                  : state.pending
                    ? t('payrollFormulas.blocked.pending')
                    : t('payrollFormulas.confirm.length', {
                        min: PAYROLL_FORMULA_CONFIRMATION_MIN,
                        max: PAYROLL_FORMULA_CONFIRMATION_MAX,
                      })}
              </p>
            )}
            {/*
              Absent while a write is pending (header §1, final review R23): closing the
              form under its own confirmation takes the refusal line with it, and that
              line is the one place the server's `details.reason` is printed —
              `ApiErrorNote` renders no details.
            */}
            {state.pending ? null : (
              <button
                type="button"
                className="wp-button"
                data-testid="u18-confirm-cancel"
                onClick={closeConfirm}
              >
                {t('payrollFormulas.confirm.cancel')}
              </button>
            )}
          </div>
        </section>
      )}

      {inForce === null ? null : (
        <section
          className="wp-section"
          aria-labelledby="u18-params-heading"
          data-testid="u18-params"
        >
          <h2 className="wp-section__title" id="u18-params-heading">
            {t('payrollFormulas.params.title', { version: String(inForce.version) })}
          </h2>
          <p className="wp-small wp-muted" data-testid="u18-effective-from">
            {t('payrollFormulas.effectiveFrom', { from: inForce.effective_from })}
          </p>

          {/* Which runs are stamped with this version — the reason it can never be
              edited, made concrete rather than asserted. Unknown is said as unknown. */}
          <p className="wp-small wp-muted" data-testid="u18-usage">
            {!props.canViewRuns || runs.status !== 'ok' || runsUsing === null
              ? t('payrollFormulas.usage.unknown')
              : runsUsing.length === 0
                ? t('payrollFormulas.usage.none')
                : t('payrollFormulas.usage.count', { count: String(runsUsing.length) })}
          </p>

          <FormulaParamsTable formula={inForce} testIdPrefix="u18" />
        </section>
      )}

      {!composing ? null : (
        <section
          className="wp-section"
          aria-labelledby="u18-composer-heading"
          data-testid="u18-composer"
        >
          <h2 className="wp-section__title" id="u18-composer-heading">
            {base === null
              ? t('payrollFormulas.composer.first')
              : t('payrollFormulas.composer.from', { version: String(base.version) })}
          </h2>
          <p className="wp-small" data-testid="u18-composer-draft">
            {t('payrollFormulas.composer.draft')}
          </p>

          {base === null ? (
            <p className="wp-small wp-muted" data-testid="u18-composer-blank">
              {t('payrollFormulas.composer.blank')}
            </p>
          ) : null}

          {/* Header §2b: said before the press, not after. */}
          {baseWarned ? (
            <StatusNote
              severity="high"
              role="alert"
              title={t('payrollFormulas.composer.noteLost.title')}
              testId="u18-note-lost"
            >
              <p className="wp-small">{t('payrollFormulas.composer.noteLost.body')}</p>
            </StatusNote>
          ) : null}

          <DateField
            id="u18-field-effective_from"
            testId="u18-field-effective_from"
            label={t('payrollFormulas.composer.effectiveFrom.label')}
            value={effectiveFrom}
            onChange={setEffectiveFrom}
          >
            <p className="wp-small wp-muted">{t('payrollFormulas.composer.effectiveFrom.hint')}</p>
          </DateField>

          <fieldset className="wp-filters__group" data-testid="u18-fields">
            <legend>{t('payrollFormulas.composer.fields')}</legend>
            {PAYROLL_FORMULA_SCALAR_PARAMS.map((key) => {
              const field = fieldOf(key);
              return (
                <div className="wp-field" key={key}>
                  <label className="wp-field__label" htmlFor={`u18-field-${key}`}>
                    {t(PARAM_LABEL[key])} <code className="wp-small">{key}</code>
                    {REQUIRED_PARAMS.has(key) ? null : (
                      <span className="wp-small wp-muted">
                        {' '}
                        {t('payrollFormulas.composer.optional')}
                      </span>
                    )}
                  </label>
                  {/* No `min`, no `max`, no `step`: KTD6 — see this file's header. */}
                  <input
                    className="wp-field__input"
                    id={`u18-field-${key}`}
                    data-testid={`u18-field-${key}`}
                    type="text"
                    inputMode="decimal"
                    value={field.text}
                    onChange={(event) => {
                      typeField(key, event.target.value);
                    }}
                  />
                  {field.invalid ? (
                    <p className="wp-small" data-testid={`u18-invalid-${key}`}>
                      {t('payrollFormulas.composer.notANumber')}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </fieldset>

          <fieldset className="wp-filters__group" data-testid="u18-bracket-fields">
            <legend>{t('payrollFormulas.brackets.title')}</legend>
            <p className="wp-small wp-muted">{t('payrollFormulas.brackets.hint')}</p>
            {bracketRows.map((row, index) => (
              <div className="wp-field" key={`bracket-${String(index)}`}>
                <label className="wp-field__label" htmlFor={`u18-bracket-bound-${String(index)}`}>
                  {t('payrollFormulas.brackets.bound', { index: String(index + 1) })}
                </label>
                <input
                  className="wp-field__input"
                  id={`u18-bracket-bound-${String(index)}`}
                  data-testid={`u18-bracket-bound-${String(index)}`}
                  type="text"
                  inputMode="decimal"
                  value={row.up_to_vnd}
                  onChange={(event) => {
                    typeBrackets(
                      bracketRows.map((one, at) =>
                        at === index ? { ...one, up_to_vnd: event.target.value } : one,
                      ),
                    );
                  }}
                />
                <label className="wp-field__label" htmlFor={`u18-bracket-rate-${String(index)}`}>
                  {t('payrollFormulas.brackets.rate', { index: String(index + 1) })}
                </label>
                <input
                  className="wp-field__input"
                  id={`u18-bracket-rate-${String(index)}`}
                  data-testid={`u18-bracket-rate-${String(index)}`}
                  type="text"
                  inputMode="decimal"
                  value={row.rate}
                  onChange={(event) => {
                    typeBrackets(
                      bracketRows.map((one, at) =>
                        at === index ? { ...one, rate: event.target.value } : one,
                      ),
                    );
                  }}
                />
                <button
                  type="button"
                  className="wp-button"
                  data-testid={`u18-bracket-remove-${String(index)}`}
                  onClick={() => {
                    typeBrackets(bracketRows.filter((_row, at) => at !== index));
                  }}
                >
                  {t('payrollFormulas.brackets.remove')}
                </button>
              </div>
            ))}
            <button
              type="button"
              className="wp-button"
              data-testid="u18-bracket-add"
              onClick={() => {
                typeBrackets([...bracketRows, { up_to_vnd: '', rate: '' }]);
              }}
            >
              {t('payrollFormulas.brackets.add')}
            </button>
            {bracketsInvalid ? (
              <p className="wp-small" data-testid="u18-brackets-invalid">
                {t('payrollFormulas.brackets.invalid')}
              </p>
            ) : null}
          </fieldset>

          {/*
            What this draft would change against the version it was composed FROM, and
            every parameter of it — not only the ones with a field above. The panel's own
            statement, next to the press it describes: the hook's diff (in the history
            section) compares the draft with the version in FORCE, which is a different
            question whenever the composer started from another row.
          */}
          {base === null ? null : (
            <div data-testid="u18-composer-changes-block">
              <h3 className="wp-small">
                {t('payrollFormulas.composer.changes.title', { version: String(base.version) })}
              </h3>
              {changes.length === 0 ? (
                <p className="wp-small wp-muted" data-testid="u18-composer-changes-none">
                  {t('payrollFormulas.composer.changes.none', { version: String(base.version) })}
                </p>
              ) : (
                <ul data-testid="u18-composer-changes">
                  {changes.map((change) => (
                    <li
                      className="wp-small"
                      key={change.key}
                      data-testid={`u18-composer-change-${change.key}`}
                    >
                      {t('payrollFormulas.composer.changes.entry', {
                        key: change.key,
                        from: change.from ?? t('payrollFormulas.composer.changes.absent'),
                        to: change.to ?? t('payrollFormulas.composer.changes.absent'),
                      })}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="wp-filters__actions">
            {canSubmit ? (
              <button
                type="button"
                className="wp-button wp-button--primary"
                data-testid="u18-submit"
                onClick={() => {
                  setLastPress('draft');
                  state.create();
                }}
              >
                {t('payrollFormulas.submit')}
              </button>
            ) : (
              <p className="wp-small" data-testid="u18-submit-blocked">
                {invalidFields.length > 0 || bracketsInvalid
                  ? t('payrollFormulas.blocked.invalid')
                  : missingRequired.length > 0
                    ? t('payrollFormulas.blocked.missing', { keys: missingRequired.join(', ') })
                    : dateMissing
                      ? t('payrollFormulas.blocked.date')
                      : unchanged
                        ? t('payrollFormulas.blocked.unchanged')
                        : t('payrollFormulas.blocked.pending')}
              </p>
            )}
            {/*
              Offered whenever anything has been TYPED, not only when the draft holds a
              parsed number: a field cleared or mistyped leaves the draft empty and the text
              on screen, and a discard control that vanished then would leave no way back.
              Never while a write is pending (header §1): discarding resets the hook's
              record of the write in flight.
            */}
            {state.pending ||
            (state.touched.length === 0 &&
              Object.keys(fieldTexts).length === 0 &&
              bracketTexts === null &&
              effectiveFrom === '') ? null : (
              <button
                type="button"
                className="wp-button"
                data-testid="u18-discard"
                onClick={() => {
                  clearComposer();
                  state.resetDraft();
                }}
              >
                {t('payrollFormulas.discard')}
              </button>
            )}
          </div>
        </section>
      )}
    </>
  );
}
